import { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import ImageUpload from '@/components/ImageUpload';
import RichTextEditor from '@/components/RichTextEditor';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const emptyArticle = { title: '', slug: '', excerpt: '', content: '', category: 'general', image_url: '', video_url: '', external_video_url: '', status: 'published' };

export default function AdminArticles() {
  const [articles, setArticles] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyArticle);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchArticles = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/articles`);
      setArticles(data);
    } catch (err) {
      console.error('Failed to load articles:', err);
      toast.error('Failed to load articles');
    }
  }, []);
  useEffect(() => { fetchArticles(); }, [fetchArticles]);

  const openNew = () => { setEditing(null); setForm(emptyArticle); setDialogOpen(true); };
  const openEdit = (a) => {
    setEditing(a.id);
    setForm({
      title: a.title, slug: a.slug, excerpt: a.excerpt, content: a.content,
      category: a.category, image_url: a.image_url || '', video_url: a.video_url || '',
      external_video_url: a.external_video_url || '', status: a.status,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      const slug = form.slug || form.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      const payload = { ...form, slug };
      if (editing) {
        await axios.put(`${API}/admin/articles/${editing}`, payload);
        toast.success('Article updated');
      } else {
        await axios.post(`${API}/admin/articles`, payload);
        toast.success('Article created');
      }
      setDialogOpen(false);
      fetchArticles();
    } catch (err) {
      console.error('Failed to save article:', err);
      toast.error('Failed to save article');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this article?')) return;
    try {
      await axios.delete(`${API}/admin/articles/${id}`);
      toast.success('Deleted');
      fetchArticles();
    } catch (err) {
      console.error('Failed to delete article:', err);
      toast.error('Failed');
    }
  };

  return (
    <div data-testid="admin-articles">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold" style={{ fontFamily: "'Poppins', sans-serif" }}>Articles</h1>
        <Button onClick={openNew} className="btn-gold text-sm" data-testid="add-article-btn"><Plus size={16} className="mr-2" />Add Article</Button>
      </div>
      <div className="bg-white rounded-sm border overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Image</TableHead>
              <TableHead>Title</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Video</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {articles.map(a => (
              <TableRow key={a.id}>
                <TableCell>
                  {a.image_url ? (
                    <img src={a.image_url} alt={a.title} className="w-10 h-10 object-cover rounded-sm" />
                  ) : (
                    <div className="w-10 h-10 bg-gray-100 rounded-sm" />
                  )}
                </TableCell>
                <TableCell className="font-medium">{a.title}</TableCell>
                <TableCell className="capitalize">{a.category}</TableCell>
                <TableCell className="text-xs text-gray-400">{a.external_video_url ? 'External' : a.video_url ? 'Hosted' : '-'}</TableCell>
                <TableCell><span className={`text-xs px-2 py-1 rounded ${a.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{a.status}</span></TableCell>
                <TableCell className="text-sm">{new Date(a.created_at).toLocaleDateString('en-NZ')}</TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <button onClick={() => openEdit(a)} className="p-1 hover:text-[#D4AF37]"><Pencil size={16} /></button>
                    <button onClick={() => handleDelete(a.id)} className="p-1 hover:text-red-500"><Trash2 size={16} /></button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="article-dialog">
          <DialogHeader><DialogTitle style={{ fontFamily: "'Poppins', sans-serif" }}>{editing ? 'Edit Article' : 'New Article'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Title *</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="article-title" /></div>
            <div><Label>Slug</Label><Input value={form.slug} onChange={e => setForm({...form, slug: e.target.value})} placeholder="auto-generated from title" data-testid="article-slug" /></div>
            <div><Label>Excerpt *</Label><Textarea value={form.excerpt} onChange={e => setForm({...form, excerpt: e.target.value})} rows={2} data-testid="article-excerpt" /></div>
            <div><Label>Content *</Label><RichTextEditor value={form.content} onChange={v => setForm({...form, content: v})} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Category</Label>
                <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="beginner">Beginner</SelectItem>
                    <SelectItem value="guide">Guide</SelectItem>
                    <SelectItem value="market-update">Market Update</SelectItem>
                    <SelectItem value="tax">Tax Notes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Status</Label>
                <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="published">Published</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <ImageUpload value={form.image_url} onChange={url => setForm({...form, image_url: url})} label="Article Image" />
            <div><Label>External Video URL</Label><Input value={form.external_video_url} onChange={e => setForm({...form, external_video_url: e.target.value})} placeholder="https://youtube.com/watch?v=..." data-testid="article-external-video" /></div>
            <Button onClick={handleSave} className="btn-gold w-full" data-testid="save-article-btn">Save Article</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
